'use client';
import { useQuery, useInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import { LOYALTY_QUERIES } from '../../infrastructure/loyalty/utils/queries';
import type {
  GetCustomerBalanceRequest,
  GetTransactionHistoryRequest,
  GetLoyaltyPolicyRequest,
  GetProductCreditPreviewRequest,
} from '../../infrastructure/loyalty/utils/types';
import { LOYALTY_QUERY_KEYS } from '../../infrastructure/loyalty/utils/keys';
import { loyaltyAPIClient } from '../../infrastructure/loyalty/loyaltyAPIClient';
import { useUser } from '../user/useUser';

export const useLoyaltyService = () => {
  const useGetCustomerBalance = (
    payload: GetCustomerBalanceRequest & {
      enabled?: boolean;
    },
  ) => {
    const { enabled, ...requestPayload } = payload;
    return useQuery({
      ...LOYALTY_QUERIES.getCustomerBalance(requestPayload),
      enabled,
    });
  };

  const useGetTransactionHistory = (
    payload: GetTransactionHistoryRequest & {
      enabled?: boolean;
    },
  ) => {
    const { enabled, ...requestPayload } = payload;
    return useQuery({
      ...LOYALTY_QUERIES.getTransactionHistory(requestPayload),
      enabled,
    });
  };

  const useGetTransactionHistoryInfinite = (
    payload: Omit<GetTransactionHistoryRequest, 'lastEvaluatedKey'> & {
      enabled?: boolean;
    },
  ) => {
    const { enabled, ...requestPayload } = payload;
    return useInfiniteQuery({
      queryKey: LOYALTY_QUERY_KEYS.getTransactionHistory({
        ...requestPayload,
        lastEvaluatedKey: undefined,
      }),
      queryFn: ({ pageParam }: { pageParam: string | undefined }) => {
        return loyaltyAPIClient.getTransactionHistory({
          ...requestPayload,
          lastEvaluatedKey: pageParam,
        });
      },
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.lastEvaluatedKey,
      enabled,
    });
  };

  const useGetLoyaltyPolicy = (
    payload: GetLoyaltyPolicyRequest & {
      enabled?: boolean;
    },
  ) => {
    const { enabled, ...requestPayload } = payload;
    return useQuery({
      ...LOYALTY_QUERIES.getLoyaltyPolicy(requestPayload),
      enabled,
    });
  };

  const useGetSuspenseCustomerBalance = (payload: GetCustomerBalanceRequest) => {
    return useSuspenseQuery(LOYALTY_QUERIES.getCustomerBalance(payload));
  };

  const useGetSuspenseLoyaltyPolicy = (payload: GetLoyaltyPolicyRequest) => {
    return useSuspenseQuery(LOYALTY_QUERIES.getLoyaltyPolicy(payload));
  };

  /** Credit the customer would earn on one product (Credit Repurchase Loop). */
  const useGetProductCreditPreview = (payload: GetProductCreditPreviewRequest) => {
    // Wait for the session to resolve: on first render `user` is still undefined for a signed-in
    // customer, and fetching then would cache the member's rate under the guest key.
    const { user, isLoading: isUserLoading } = useUser();
    return useQuery({
      ...LOYALTY_QUERIES.getProductCreditPreview({ ...payload, customerId: user?.userId }),
      enabled: !isUserLoading,
    });
  };

  return {
    useGetCustomerBalance,
    useGetSuspenseCustomerBalance,
    useGetTransactionHistory,
    useGetTransactionHistoryInfinite,
    useGetLoyaltyPolicy,
    useGetSuspenseLoyaltyPolicy,
    useGetProductCreditPreview,
  };
};
